/**
 * One exam, on its own page — name, type, and every paper it is made of.
 *
 * A page rather than a dialog because an exam is not a small edit: the boards
 * are twelve papers across a fortnight, each with its own date, subjects and
 * label. A modal that size is a page wearing a costume, and it cannot be
 * linked to or reloaded.
 *
 * Papers are listed here and edited in a dialog. They were inline fields while
 * a paper was four short ones; a paper also says WHICH CHAPTERS it examines,
 * and thirty of those across several subjects is not a table cell.
 *
 * Still staged, which is the part that matters: the page owns the whole edit
 * until Save, dialog included, so a half-finished timetable is never visible to
 * the thirty other people who read it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Attribute, AttributeGrid, BackButton, Button, DataTable, Input, Select,
  type ColumnDef,
} from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { classSharedApi, schoolsApi } from '../api/schoolsApi';
import { EXAM_TYPE_LABELS, type ClassExamType, type ExamPaper } from '@shared-ui/class-shared';
import { CLASS_SUBJECTS } from './Schools';
import { ExamPaperDialog } from './ExamPaperDialog';
import { useExamChapters } from './useExamChapters';
import './Schools.css';

/** A blank paper. `id` empty means the server mints one; an existing paper
 *  keeps its id so a moved date stays the same paper. */
const blankPaper = (): ExamPaper => ({ id: '', subjects: [], date: '' });

export function ExamDetail() {
  const { schoolId = '', classId = '', examId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const subjectLabel = useSubjectLabel();
  // `?section=` says WHOSE exam this is — one section's, or (absent) the whole class's.
  // It has to travel on the URL rather than in component state because this page is
  // linkable and reloadable, and the level decides which route the save goes to.
  const [search] = useSearchParams();
  const sectionId = search.get('section') || undefined;
  const owner = useMemo(() => ({ schoolClassId: classId, sectionId }), [classId, sectionId]);

  const { data: classes } = useQuery({
    queryKey: ['school-classes', schoolId],
    queryFn: () => schoolsApi.listClasses(schoolId),
    enabled: !!schoolId,
  });
  const cls = useMemo(
    () => (classes?.classes ?? []).find(c => c.id === classId),
    [classes, classId],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['class-exams', classId, sectionId ?? ''],
    queryFn: () => classSharedApi.listExams(owner),
    enabled: !!classId,
  });
  const exam = useMemo(
    () => (data?.exams ?? []).find(e => e.id === examId),
    [data, examId],
  );

  const { data: sectionData } = useQuery({
    queryKey: ['class-sections', classId],
    queryFn: () => schoolsApi.listSections(classId),
    enabled: !!classId,
  });
  const sections = useMemo(() => sectionData?.sections ?? [], [sectionData]);
  const sectionName = sections.find(x => x.id === sectionId)?.name;

  const isNew = examId === 'new';
  const [name, setName] = useState('');
  const [type, setType] = useState<ClassExamType>('unit-test');
  const [papers, setPapers] = useState<ExamPaper[]>([blankPaper()]);
  /**
   * Which level this exam sits at, as an EDITABLE value — the URL only seeds it.
   *
   * Without it, an exam filed at the wrong level could only be fixed by deleting and
   * retyping it, which loses the papers and the id every deadline names.
   */
  const [level, setLevel] = useState<string>(sectionId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Seed once from the server, then leave the form alone — refetching must not
  // stamp on an edit in progress.
  useEffect(() => {
    if (isNew || loaded || !exam) return;
    setName(exam.name);
    setType(exam.type);
    setPapers(exam.papers.length ? exam.papers : [blankPaper()]);
    setLoaded(true);
  }, [exam, isNew, loaded]);

  /** Subjects this CLASS teaches, falling back to the catalogue when it has not
   *  said. Offering a subject the class does not teach records a paper nobody
   *  in the room sits. */
  const subjectOptions = useMemo(() => {
    // The class's own list, plus anything the papers ALREADY carry. Without the
    // second half an extended subject (english_grammar, hindi) had no option to
    // match, and the control fell back to printing the raw key.
    const base = cls?.subjects?.length ? cls.subjects : CLASS_SUBJECTS;
    const used = papers.flatMap(p => p.subjects);
    return [...new Set([...base, ...used])].map(s => ({ value: s, label: subjectLabel(s) }));
  }, [cls?.subjects, papers, subjectLabel]);

  /** What a paper's label defaults to: the subjects it examines. Kept in step
   *  with the subjects until someone types their own, at which point the label
   *  is theirs and nothing rewrites it. */
  const labelFor = (subjects: string[]) => subjects.map(subjectLabel).join(', ');

  const save = useMutation({
    /* `nextPapers` is passed rather than read from state: a paper saved from the
       dialog is written in the same tick it is added, and `papers` would still
       be the array from before it. */
    mutationFn: async (opts: { papers?: ExamPaper[]; stay?: boolean } = {}) => {
      const body = { name: name.trim(), type, papers: opts.papers ?? papers };
      if (isNew) return classSharedApi.createExam({ schoolClassId: classId, sectionId: level || undefined }, body);
      // The level FIRST, so the update lands wherever the exam now lives. The other order
      // writes the papers to the old owner and then moves a document that is already
      // stale by one edit.
      if ((level || undefined) !== sectionId) {
        await classSharedApi.moveExam(owner, examId, level || null);
      }
      return classSharedApi.updateExam(
        { schoolClassId: classId, sectionId: level || undefined }, examId, body);
    },
    onSuccess: (_res, opts) => {
      qc.invalidateQueries({ queryKey: ['class-exams', classId] });
      // A paper saved from its dialog leaves the reader on this page — they are
      // very likely adding the next one. Only the page's own Save leaves.
      if (!opts?.stay) navigate(`/schools/${schoolId}/classes/${classId}?tab=exams`);
    },
    onError: (e: Error) => setError(e.message),
  });

  /** Which paper the dialog is open on: an index to edit, 'new', or closed. */
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  /* Named chapters for the table. The union of every paper's subjects, so one
     resolution serves the whole page — and the same one the dialog uses, so a
     row cannot name chapters the dialog does not offer. */
  const paperSubjects = useMemo(
    () => [...new Set(papers.flatMap(p => p.subjects))],
    [papers],
  );
  const { nameById, allIds } = useExamChapters(paperSubjects, cls?.board, cls?.grade);

  const columns: ColumnDef<ExamPaper & { _i: number }>[] = useMemo(() => [
    {
      key: 'subjects', header: 'Subjects', width: '22%',
      render: p => (p.subjects.length ? p.subjects.map(subjectLabel).join(', ') : <span className="dc-empty">—</span>),
    },
    {
      key: 'date', header: 'Date', width: '14%',
      render: p => p.date || <span className="dc-empty">—</span>,
    },
    {
      key: 'label', header: 'Label', width: '24%',
      // The derived label is shown dimmed: it is what the paper WOULD be called,
      // not something anybody typed, and the two should not read alike.
      render: p => (p.label
        ? p.label
        : <span className="dc-empty">{labelFor(p.subjects) || '—'}</span>),
    },
    {
      /* The chapters BY NAME. "7 chapters" is a number a reader then has to open
         the paper to understand, and the whole point of scoping an exam is which
         ones — so the row says which ones.

         Two ways to be the whole syllabus, and both say so rather than listing
         thirty names: an ABSENT scope, which is what the field means by default,
         and a scope that happens to name every chapter there is. The second
         matters because ticking "the whole syllabus" off and on again leaves an
         explicit list, and a reader should not be able to tell. */
      key: 'scope', header: 'Chapters', width: '26%',
      render: p => {
        if (p.scope === undefined) return <span className="dc-empty">Whole syllabus</span>;
        if (allIds.length > 0 && p.scope.length >= allIds.length
          && allIds.every(id => p.scope!.includes(id))) {
          return <span className="dc-empty">Whole syllabus</span>;
        }
        // An id with no name is a chapter the curriculum no longer has. Shown as
        // the id rather than dropped, because a silently shorter list is worse.
        const names = p.scope.map(id => nameById.get(id) ?? id);
        return names.length ? <span className="exam-scope-cell">{names.join(', ')}</span> : <span className="dc-empty">—</span>;
      },
    },
    {
      key: 'maxMarks', header: 'Max marks', width: '10%',
      render: p => (p.maxMarks != null ? String(p.maxMarks) : <span className="dc-empty">—</span>),
    },
    {
      key: 'actions', header: '', width: '8%', hideable: false,
      render: p => (
        <span className="school-row-actions">
          <button
            type="button"
            className="school-row-icon"
            aria-label="Edit this paper"
            onClick={() => setEditing(p._i)}
          >✎</button>
          <button
            type="button"
            className="school-row-icon school-row-icon--danger"
            aria-label="Remove this paper"
            // The last one stays: an exam with no papers has no date and no
            // scope, so it is not an exam — delete the exam instead.
            disabled={papers.length === 1}
            onClick={() => setPapers(ps => ps.filter((_, n) => n !== p._i))}
          >×</button>
        </span>
      ),
    },
  ], [papers.length, subjectLabel, nameById, allIds]);

  const submit = () => {
    if (!name.trim()) { setError('The exam needs a name.'); return; }
    if (papers.some(p => !p.date || p.subjects.length === 0)) {
      setError('Every paper needs a date and at least one subject.');
      return;
    }
    setError(null);
    save.mutate({});
  };

  if (!isNew && isLoading) {
    return (
      <div className="submissions-page">
        <BackButton to={`/schools/${schoolId}/classes/${classId}?tab=exams`} label="class" />
        <p className="school-hint">Loading…</p>
      </div>
    );
  }

  return (
    <div className="submissions-page">
      <BackButton to={`/schools/${schoolId}/classes/${classId}?tab=exams`} label="class" />

      <div className="page-header">
        <h1>{isNew ? 'New exam' : (exam?.name ?? 'Exam')}</h1>
        <p className="school-hint">
          {cls ? `Grade ${cls.grade} · ${cls.academicYear}` : ''}
          {' · '}
          {/* Which level, always — the difference between a date thirty people read and a
              date ninety do is the first thing to know before changing one. */}
          {sectionId
            ? (sectionName ? `${sectionName} only` : 'one section only')
            : 'sat by the whole class'}
          {' · '}
          {/* The span, because an exam with twelve papers runs over a fortnight
              and one date would misrepresent it. */}
          {papers.filter(p => p.date).length
            ? (() => {
              const ds = papers.map(p => p.date).filter(Boolean).sort();
              return ds[0] === ds[ds.length - 1] ? ds[0] : `${ds[0]} → ${ds[ds.length - 1]}`;
            })()
            : 'no dates yet'}
        </p>
      </div>

      <section className="school-section">
        <div className="school-detail-head">
          {error && <span className="school-error">{error}</span>}
          <Button variant="secondary" size="sm" disabled={save.isPending}
            onClick={() => navigate(`/schools/${schoolId}/classes/${classId}?tab=exams`)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>

        <AttributeGrid>
          <Attribute label="Name">
            <span className="sui-attr-value">
              <Input value={name} placeholder="Unit Test 2" onChange={e => setName(e.target.value)} />
            </span>
            </Attribute>
          <Attribute label="Type">
            <span className="sui-attr-value">
              <Select value={type} onChange={e => setType(e.target.value as ClassExamType)}>
                {(Object.keys(EXAM_TYPE_LABELS) as ClassExamType[]).map(t => (
                  <option key={t} value={t}>{EXAM_TYPE_LABELS[t]}</option>
                ))}
              </Select>
            </span>
            </Attribute>
          {/* Only where there is a choice to make: a class with no sections has exactly
              one level, and a picker offering one option is furniture. */}
          {sections.length > 0 && (
            <Attribute label={`${EXAM_TYPE_LABELS[type] ?? type} for`}>
              <span className="sui-attr-value">
                <Select value={level} onChange={e => setLevel(e.target.value)}>
                  <option value="">The whole class</option>
                  {sections.map(x => <option key={x.id} value={x.id}>{x.name} only</option>)}
                </Select>
              </span>
            </Attribute>
          )}
        </AttributeGrid>

        {!isNew && (level || undefined) !== sectionId && (
          <p className="school-hint">
            Saving moves this exam to {level ? `${sections.find(x => x.id === level)?.name ?? 'that section'} only`
              : 'the whole class'}. It keeps its papers and its id, so anything already
            counting down to it still is.
          </p>
        )}

        <h2 className="school-subhead">Papers</h2>

        <DataTable
          columns={columns}
          data={papers.map((p, i) => ({ ...p, _i: i }))}
          rowKey={p => p.id || `new-${p._i}`}
          compact
          emptyMessage="No papers yet."
        />

        <div className="school-row">
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
            + Add a paper
          </Button>
        </div>

        <ExamPaperDialog
          isOpen={editing !== null}
          paper={typeof editing === 'number' ? papers[editing] : null}
          subjectOptions={subjectOptions}
          board={cls?.board}
          grade={cls?.grade}
          onClose={() => setEditing(null)}
          /* Saving a paper WRITES it, rather than staging it behind the page's
             own Save. A dialog with a Save button that does not save is the
             thing a reader is entitled to disbelieve — and an exam is edited one
             paper at a time over days, so each is its own piece of work.

             The exception is an exam that does not exist yet: there is nothing
             to add a paper TO until the page's Save mints it, so a new exam's
             papers stay staged and go up with it. */
          saving={save.isPending}
          persists={!isNew}
          onSave={p => {
            const next = editing === 'new'
              ? [...papers, p]
              : papers.map((old, n) => (n === editing ? p : old));
            setPapers(next);
            setEditing(null);
            if (!isNew) save.mutate({ papers: next, stay: true });
          }}
        />

      </section>
    </div>
  );
}
